import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { ScriptMarkupBreadcrumb, getCloudinarySrcSet, getCloudinaryUrl } from 'components/rich';
import useDataContext from 'data/context/data';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { isFreeShippingOnly } from 'ecommerce/lib/coupon';

import BudgetTiles, { BUDGET_TILES } from '../components/gifts/budgetTiles';
import {
  BudgetGrid,
  BudgetHero,
  BudgetStickyBar,
  UseCases,
  cardId,
  loadBudgetContent,
} from '../components/gifts/budgetSections';
import CouponStrip from '../components/gifts/couponStrip';
import FinishingTouches from '../components/gifts/finishingTouches';
import LandingEditorial from '../components/landing/editorial';
import { HERO_IMAGE_SIZES } from '../components/landing/hero';
import { resolveIdeas } from '../components/gifts/giftIdea';
import LandingStory from '../components/landing/story';
import RecipientList from '../components/gifts/recipientList';
import { ctaTracker, landingImage, productImage } from '../components/landing/shared';
import { Head, Layout } from '../components/layout';
import useInView from '../lib/useInView';
import useScrolledPast from '../lib/useScrolledPast';
import SETTINGS from '../settings';

// Gifts under $50 (docs/gifts-page.md#budget-pages). Explicit page file, so it wins over the
// category route in [...params].js. The product list comes from the category spec, title,
// meta, H1 and editorial from content/categories/gifts-under-50.md, the rest is design copy.
const CATEGORY_ID = 'gifts-under-50';
const track = ctaTracker('gifts_budget_page', { budget: 50 });

const HERO_IMAGES = {
  block: productImage('engineer-prints/3'),
  couple: landingImage('hc-hands-open'),
  tiny: productImage('news-print/1'),
};

// The use case each product is merchandised for on this page.
const CARD_BADGES = {
  'engineer-prints': 'THE WHOLE FAMILY',
  artprint: 'FOR YOUR PERSON',
  'metal-wall-prints': 'MODERN WALLS',
  'hardcover-book': 'FOR MOM & DAD',
  'news-print': 'NEW HOME',
  'wall-calendar': 'FOR GRANDPARENTS',
  'wall-calendar-large': 'FAMILY PLANNER',
  'wooden-rails': 'READY TO HANG',
};

// Recipients jump to the product card that fits them best.
const RECIPIENTS = [
  {
    name: 'Mom & Dad',
    hint: 'thirty years, one book',
    target: cardId('hardcover-book'),
    image: landingImage('hc-cover-held'),
  },
  {
    name: 'Your person',
    hint: 'the year you met, on the wall',
    target: cardId('artprint'),
    image: productImage('artprint/1'),
  },
  {
    name: 'Grandparents',
    hint: 'a new grandkid every month',
    target: cardId('wall-calendar'),
    image: productImage('wall-calendar/3'),
  },
  {
    name: 'New home',
    hint: 'the first thing on the wall',
    target: cardId('news-print'),
    image: productImage('news-print/1'),
  },
  {
    name: 'New parents',
    hint: 'the first year, in a book',
    target: cardId('hardcover-book'),
    image: landingImage('hc-couch-open'),
  },
  {
    name: 'The whole family',
    hint: 'everyone, three feet wide',
    target: cardId('engineer-prints'),
    image: productImage('engineer-prints/3'),
  },
];

// No pairings here: every print + rails pair is over $50.
const IDEAS = [
  {
    id: 'idea-coffee-table',
    recipient: 'Coffee table',
    title: 'A book for the coffee table',
    body: 'An 8x8 Hardcover Photo Book with matte pages. The one guests pick up first.',
    productNames: ['8x8 Hardcover Photo Book'],
    products: ['hardcover-book'],
    image: landingImage('hero-coffee-table'),
  },
  {
    id: 'idea-bedroom',
    recipient: 'Bedroom',
    title: 'Wall art that arrives ready to hang',
    body: 'A Metal Wall Print in glossy aluminum. It sits 1/2 in off the wall, and the hanging hardware is in the box.',
    short: 'Glossy aluminum, hanging hardware in the box.',
    productNames: ['Metal Wall Print'],
    products: ['metal-wall-prints'],
    image: productImage('metal-wall-prints/1'),
  },
  {
    id: 'idea-home-office',
    recipient: 'Home office',
    title: 'A new photo every month',
    body: 'The Keepsake Wall Calendar holds 14 of your photos. Each one detaches when its month is over, so nothing gets thrown away.',
    short: '14 of your photos, each one detaches when its month is over.',
    productNames: ['Keepsake Wall Calendar'],
    products: ['wall-calendar'],
    image: productImage('wall-calendar/2'),
  },
];

const BUDGETS = [BUDGET_TILES.under20, BUDGET_TILES.giftCards, BUDGET_TILES.gifts];

const GiftsUnder50Page = ({ content, cardMeta }) => {
  const dataContext = useDataContext();
  const claimedCoupon = useClaimedCoupon();
  const { previewSummary, summary } = claimedCoupon;
  const heroRef = useRef();
  const gridEndRef = useRef();
  const heroInView = useInView(heroRef);
  const pastGrid = useScrolledPast(gridEndRef);

  const category = dataContext.getCategory(CATEGORY_ID);
  const gifts = dataContext.getCategory('gifts');
  const breadcrumb = [
    { name: 'Home', url: '/' },
    { name: gifts.name, url: gifts.url },
    { name: 'Under $50' },
  ];
  const products = category.data
    .flatMap((section) => section.products)
    .map((product) => ({ product, meta: cardMeta[product.id], badge: CARD_BADGES[product.id] }));

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={getCloudinaryUrl({
          imageKey: HERO_IMAGES.block.imageKey,
          prefix: HERO_IMAGES.block.prefix,
        })}
        heroImageSrcSet={getCloudinarySrcSet(HERO_IMAGES.block)}
        heroImageSizes={HERO_IMAGE_SIZES}
      />
      <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />

      <Layout>
        <div className="lnd gft">
          <CouponStrip claimedCoupon={claimedCoupon} />

          <BudgetHero
            ref={heroRef}
            category={category}
            breadcrumb={breadcrumb}
            lead={{
              desktop:
                'One gift that does the heavy lifting. Wall art, a hardcover photo book or a year of photos on the wall, for the people closest to you.',
              phone: 'One gift that does the heavy lifting, for the people closest to you.',
            }}
            giftCount={products.length}
            images={HERO_IMAGES}
            tag={{ to: 'To Mom & Dad', note: ['Thirty years,', 'one book. Sam'] }}
            track={track}
          />

          <RecipientList
            eyebrow="One gift, done well"
            title="Who’s the big gift for?"
            aside="Fewer people, bigger moments. These are the gifts for parents, partners and anyone who says they don’t need anything."
            recipients={RECIPIENTS}
            track={track}
          />

          <BudgetGrid
            ref={gridEndRef}
            eyebrow="All gifts under $50"
            title="Eight gifts, all $50 or less"
            products={products}
            columns={4}
            previewSummary={previewSummary}
            track={track}
          />

          <UseCases
            eyebrow="Where will it live?"
            title="Pick the spot, then the gift"
            aside="A gift at this price should be seen every day. Start with the place they’ll keep it."
            ideas={resolveIdeas(IDEAS, dataContext, previewSummary)}
            track={track}
          />

          <LandingStory
            image={productImage('square-engineer-prints/2')}
            quote="This 3 ft x 4 ft Engineer Print from Parabo Press is such a statement in our house and it’s so easy to change the photo out."
            author="@becca.spooner"
            product="Engineer Prints"
          />

          <BudgetTiles
            title="Smaller gifts, or let them choose?"
            tiles={BUDGETS}
            couponClaimed={Boolean(summary) && !isFreeShippingOnly(summary)}
            track={track}
          />

          <FinishingTouches track={track} />

          <LandingEditorial
            eyebrow="About gifts under $50"
            title="Photo gifts under $50 that feel like the main gift"
            html={content.extraDescription}
          />

          <BudgetStickyBar
            visible={!heroInView}
            pastGrid={pastGrid}
            products={products}
            previewSummary={previewSummary}
            track={track}
          />
        </div>
      </Layout>
    </>
  );
};

GiftsUnder50Page.getInitialProps = () => loadBudgetContent(CATEGORY_ID);

GiftsUnder50Page.propTypes = {
  content: PropTypes.object.isRequired,
  cardMeta: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default GiftsUnder50Page;
